import { fetchAllPages } from '@frontend/api-utils';
import { SlotRendering } from '@frontend/common-types';
import { ConstraintClient } from '@frontend/constraint/api';
import {
    Constraint,
    ConstraintMode,
    ConstraintType,
    CreateConstraint,
    isEquipmentConstraintData,
    isProductConstraintData,
    isStockConstraintData
} from '@frontend/constraint/types';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { ProductClient, ProductKitClient } from '@frontend/product/api';
import { ProductType } from '@frontend/product/types';
import { useAccountRepository } from '@frontend/repository';
import { Spot } from '@frontend/spot/types';
import { ToastUtil } from '@frontend/toast-utils';
import { useCallback, useRef, useState } from 'react';

export const ADD_TO_SLOT_MODAL_ID = 'add-to-slot-modal';

export interface AddToSlotModalProps {
    entityType: 'product' | 'equipment';
    entityId: string;
    entityName: string;
    productType?: ProductType;
}

interface SlotConstraintInfo {
    constrainedSlotIds: Set<string>;
    otherConstraintsBySlotId: Map<string, Constraint[]>;
}

export const useAddToSlotModal = (props: AddToSlotModalProps) => {
    const { store } = useAccountRepository();
    const { closeTopModal } = useModals();
    const [name, setName] = useState(props.entityName);
    const [selectedSpot, setSelectedSpot] = useState<Spot | null>(null);
    const [spotId, setSpotId] = useState<string | null>(null);
    const [moduleId, setModuleId] = useState<string | null>(null);
    const [slotId, setSlotId] = useState<string | null>(null);
    const [slotName, setSlotName] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [slotConstraintInfo, setSlotConstraintInfo] = useState<SlotConstraintInfo>({
        constrainedSlotIds: new Set(),
        otherConstraintsBySlotId: new Map()
    });
    const constraintRequestId = useRef(0);

    const updateSlotConstraintInfo = useCallback((nextInfo: SlotConstraintInfo) => {
        setSlotConstraintInfo((currentInfo) => (areSlotConstraintInfoEqual(currentInfo, nextInfo) ? currentInfo : nextInfo));
    }, []);

    const fetchConstrainedSlotIds = async (spot: Spot | null) => {
        const requestId = ++constraintRequestId.current;

        if (!spot?.id) {
            updateSlotConstraintInfo(createEmptySlotConstraintInfo());
            return;
        }

        try {
            const constraintsPromise = fetchAllPages((params) => ConstraintClient.fetchConstraints(params), {
                spot_id: spot.id
            });

            const productIds =
                props.entityType === 'product' && props.productType === ProductType.KIT ? await fetchKitProductIds(props.entityId) : new Set([props.entityId]);

            const constraints = await constraintsPromise;
            if (requestId !== constraintRequestId.current) return;

            updateSlotConstraintInfo(getSlotConstraintInfo(constraints, props, productIds));
        } catch {
            if (requestId === constraintRequestId.current) {
                updateSlotConstraintInfo(createEmptySlotConstraintInfo());
            }
        }
    };

    const handleSpotChange = async (spot: Spot | null) => {
        setSelectedSpot(spot);
        setSpotId(spot?.id ?? null);
        setModuleId(null);
        setSlotId(null);
        setSlotName(null);
        await fetchConstrainedSlotIds(spot);
    };

    const handleSlotClick = (slot: SlotRendering) => {
        if (isSlotDisabled(slot)) return;

        setSpotId(slot.spot_id);
        setModuleId(slot.module_id);
        setSlotId(slot.id);
        setSlotName(slot.display || slot.number || slot.id);
    };

    const hasExistingConstraint = (slot: SlotRendering) => slotConstraintInfo.constrainedSlotIds.has(slot.id);

    const getOtherConstraints = (slot: SlotRendering) => slotConstraintInfo.otherConstraintsBySlotId.get(slot.id) ?? [];

    const hasOtherConstraints = (slot: SlotRendering) => getOtherConstraints(slot).length > 0;

    const isSlotDisabled = (slot: SlotRendering) => isTerminalSlot(slot) || hasExistingConstraint(slot);

    const getSlotColor = (slot: SlotRendering) => {
        if (slot.id === slotId) return 'bg-status-success';
        return getDefaultSlotColor(slot);
    };

    const submitDisabled = !spotId || !slotId || !slotName || !name || isSubmitting;

    const fetchSlotConstraints = async (): Promise<Constraint[]> => {
        if (!spotId || !slotId) return [];
        return await fetchAllPages((params) => ConstraintClient.fetchConstraints(params), {
            spot_id: spotId,
            slot_id: slotId
        });
    };

    const handleKitSubmit = async () => {
        if (!spotId || !slotId || !slotName) return;

        const kitEntries = await fetchAllPages((params) => ProductKitClient.fetchProductKits(params), {
            kit_id: props.entityId
        });

        const quantityByProductId = new Map<string, number>();
        kitEntries.forEach((entry) => {
            quantityByProductId.set(entry.product_id, (quantityByProductId.get(entry.product_id) ?? 0) + entry.quantity);
        });

        const productIds = [...quantityByProductId.keys()];
        if (productIds.length === 0) return;

        const [slotConstraints, productResponse] = await Promise.all([fetchSlotConstraints(), ProductClient.resolveProducts(productIds)]);
        const productById = new Map(productResponse.results.map((product) => [product.id, product]));
        const constrainedProductIds = new Set(
            slotConstraints
                .map((constraint) => {
                    if (constraint.type !== ConstraintType.PRODUCT || !isProductConstraintData(constraint.data)) return null;
                    return constraint.data.product_id ?? null;
                })
                .filter((productId): productId is string => !!productId)
        );

        const productConstraints: CreateConstraint[] = productIds
            .filter((productId) => !constrainedProductIds.has(productId))
            .map((productId) => ({
                name: `${name} - ${slotName} - ${productById.get(productId)?.name ?? productId}`,
                type: ConstraintType.PRODUCT,
                mode: ConstraintMode.ONLY,
                slot_id: slotId,
                module_id: moduleId,
                data: { product_id: productId }
            }));

        const totalQuantity = [...quantityByProductId.values()].reduce((sum, quantity) => sum + quantity, 0);
        const stockConstraint = slotConstraints.find((constraint) => constraint.type === ConstraintType.STOCK && isStockConstraintData(constraint.data));
        const currentStockQuantity = stockConstraint && isStockConstraintData(stockConstraint.data) ? stockConstraint.data.quantity : null;

        await Promise.all(productConstraints.map((constraint) => ConstraintClient.postSpotConstraint(store.currentAccount!, spotId, constraint)));

        if (!stockConstraint) {
            await ConstraintClient.postSpotConstraint(store.currentAccount!, spotId, {
                name: `${name} - ${slotName} - stock count`,
                type: ConstraintType.STOCK,
                mode: ConstraintMode.PREFERABLY,
                slot_id: slotId,
                module_id: moduleId,
                data: { quantity: totalQuantity }
            });
        } else if (currentStockQuantity === null || currentStockQuantity === undefined || currentStockQuantity < totalQuantity) {
            await ConstraintClient.patchSpotConstraint(store.currentAccount!, spotId, stockConstraint.id, {
                data: { quantity: totalQuantity }
            });
        }
    };

    const handleSubmit = async () => {
        if (!spotId || !slotId || !slotName) return;

        setIsSubmitting(true);
        try {
            if (props.entityType === 'product' && props.productType === ProductType.KIT) {
                await handleKitSubmit();
                ToastUtil.success(CommonMessage.TOAST.PROMISE.SUCCESS, CommonMessage.TOAST.CREATE.SUCCESS(CommonMessage.OBJECTS.CONSTRAINT.DEFAULT));
                closeTopModal();
                return;
            }

            const constraint: CreateConstraint = {
                name: `${name} - ${slotName}`,
                type: props.entityType === 'product' ? ConstraintType.PRODUCT : ConstraintType.EQUIPMENT,
                mode: ConstraintMode.ONLY,
                slot_id: slotId,
                module_id: moduleId,
                data: props.entityType === 'product' ? { product_id: props.entityId } : { equipment_id: props.entityId }
            };

            await ConstraintClient.postSpotConstraint(store.currentAccount!, spotId, constraint);
            ToastUtil.success(CommonMessage.TOAST.PROMISE.SUCCESS, CommonMessage.TOAST.CREATE.SUCCESS(CommonMessage.OBJECTS.CONSTRAINT.DEFAULT));
            closeTopModal();
        } catch {
            ToastUtil.error(CommonMessage.TOAST.PROMISE.ERROR);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        currentAccount: store.currentAccount!,
        name,
        setName,
        selectedSpot,
        spotId,
        moduleId,
        slotId,
        slotName,
        isSubmitting,
        submitDisabled,
        hasExistingConstraint,
        hasExistingConstraints: slotConstraintInfo.constrainedSlotIds.size > 0,
        getOtherConstraints,
        hasOtherConstraints,
        hasOtherConstraintsOnSpot: slotConstraintInfo.otherConstraintsBySlotId.size > 0,
        isSlotDisabled,
        handleSpotChange,
        handleSlotClick,
        getSlotColor,
        handleSubmit
    };
};

const fetchKitProductIds = async (kitId: string): Promise<Set<string>> => {
    const kitEntries = await fetchAllPages((params) => ProductKitClient.fetchProductKits(params), {
        kit_id: kitId
    });

    return new Set(kitEntries.map((entry) => entry.product_id));
};

const createEmptySlotConstraintInfo = (): SlotConstraintInfo => ({
    constrainedSlotIds: new Set(),
    otherConstraintsBySlotId: new Map()
});

const getSlotConstraintInfo = (constraints: Constraint[], props: AddToSlotModalProps, productIds: Set<string>): SlotConstraintInfo => {
    const constrainedSlotIds = new Set<string>();
    const otherConstraintsBySlotId = new Map<string, Constraint[]>();

    constraints.forEach((constraint) => {
        if (!constraint.slot_id) return;

        if (isCurrentItemConstraint(constraint, props, productIds)) {
            constrainedSlotIds.add(constraint.slot_id);
            return;
        }

        otherConstraintsBySlotId.set(constraint.slot_id, [...(otherConstraintsBySlotId.get(constraint.slot_id) ?? []), constraint]);
    });

    return {
        constrainedSlotIds,
        otherConstraintsBySlotId
    };
};

const isCurrentItemConstraint = (constraint: Constraint, props: AddToSlotModalProps, productIds: Set<string>): boolean => {
    if (props.entityType === 'equipment') {
        return constraint.type === ConstraintType.EQUIPMENT && isEquipmentConstraintData(constraint.data) && constraint.data.equipment_id === props.entityId;
    }

    return (
        constraint.type === ConstraintType.PRODUCT &&
        isProductConstraintData(constraint.data) &&
        !!constraint.data.product_id &&
        productIds.has(constraint.data.product_id)
    );
};

const areSlotConstraintInfoEqual = (left: SlotConstraintInfo, right: SlotConstraintInfo): boolean => {
    return (
        areSetsEqual(left.constrainedSlotIds, right.constrainedSlotIds) &&
        left.otherConstraintsBySlotId.size === right.otherConstraintsBySlotId.size &&
        [...left.otherConstraintsBySlotId].every(([slotId, constraints]) => {
            const nextConstraints = right.otherConstraintsBySlotId.get(slotId);
            return (
                !!nextConstraints &&
                constraints.length === nextConstraints.length &&
                constraints.every((constraint, index) => constraint.id === nextConstraints[index].id)
            );
        })
    );
};

const areSetsEqual = <T>(left: Set<T>, right: Set<T>): boolean => {
    if (left.size !== right.size) return false;
    return [...left].every((value) => right.has(value));
};

const getDefaultSlotColor = (slot: SlotRendering): string => {
    if (isTerminalSlot(slot)) return 'bg-brand-disabled';
    if (slot.occupied) return 'bg-brand-muted';

    switch (slot.status) {
        case 'broken':
            return 'bg-status-danger';
        case 'merged':
            return 'bg-status-warning';
        default:
            return 'bg-brand-primary';
    }
};

const isTerminalSlot = (slot: SlotRendering): boolean => slot.type === 'terminal';
